import { languageDisplayLabel, LanguagesEnum } from '@/api/enums';
import {
  CampaignCatalogueItem,
  CampaignStatusMessages,
  phishingSignsLabels,
} from '@/client/campaigns';
import { Subjects } from '@/client/users';
import {
  PHISHING_BOOK_DEMO_DK,
  PHISHING_BOOK_DEMO_EN,
} from '@/common/constants/external-urls';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants/routes';
import { LanguageContext } from '@/common/context/LanguageContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import CalendarIcon from '@/images/icons/calendar.svg';
import CampaignThumbnailPlaceholder from '@/images/placeholders/campaign-thumbnail-placeholder.svg';
import { AppButton } from '@/ui/buttons';
import AppChip from '@/ui/chip/AppChip';
import { StarRating } from '@/ui/star-rating';
import { FlexContainer } from '@/ui/styled-ui';
import { Card } from 'primereact/card';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { CatalogueCardMetrics } from './catalogue';

const StyledCard = styled(Card)<{ $hasActiveSimulation: boolean }>`
  min-width: 280px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  background-color: var(--beige-main);
  transition: box-shadow 0.2s ease;
  border-radius: var(--semi-medium-border-radius);

  .p-card-body {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: var(--default-padding);
  }

  .p-card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  ${({ $hasActiveSimulation }) =>
    $hasActiveSimulation &&
    `
    .p-chip {
      background: #d9d9d9 !important;
      color: rgba(0, 0, 0, 0.5) !important;
    }

    .p-button.primary-action-button {
      background: rgba(0, 0, 0, 0.5) !important;
      border: 0 !important;

      &:hover {
        background: rgba(0, 0, 0, 0.5) !important;
        border: 0 !important;
      }
    }

    .p-button.preview-button {
      color: rgba(0, 0, 0, 0.5) !important;

      .p-button-label {
        border-bottom-color: rgba(0, 0, 0, 0.5) !important;
      }

      &:hover {
        color: rgba(0, 0, 0, 0.5) !important;

        .p-button-label {
          border-bottom-color: rgba(0, 0, 0, 0.5) !important;
        }
      }
    }

    h3 {
      color: rgba(0, 0, 0, 0.5) !important;
    }
  `}
`;

const StyledTopRow = styled(FlexContainer)`
  height: 30px;
  margin-bottom: var(--small-padding);
  position: relative;

  .p-chip {
    font-size: var(--xsmall-font-size);
    height: 20px;
  }
`;

const StyledCalendarIcon = styled.img`
  width: 19px;
  height: 19px;
  margin-left: var(--xsmall-padding);
  flex-shrink: 0;
  display: block;
`;

const StyledCampaignName = styled.h3`
  height: 22px;
  font-size: var(--semi-big-font-size);
  font-weight: 700;
  line-height: 1.2;
  margin: var(--medium-padding) 0 0 0;
  color: var(--black-main);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const StyledLanguageLabel = styled.div`
  font-size: var(--xsmall-font-size);
  color: var(--gray-deep);
  margin-top: var(--xxsmall-padding);
  height: var(--xsmall-line-height);
`;

const StyledDescription = styled.div`
  font-size: var(--small-font-size);
  color: var(--gray-deep);
  line-height: 1.4;
  margin: var(--medium-padding) 0;
  flex: 1;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 60px;
`;

const StyledThumbnailContainer = styled.div<{ $hasActiveSimulation?: boolean }>`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--box-border-radius);
  margin: 0 0 var(--medium-padding) 0;
  overflow: hidden;
  position: relative;
  opacity: ${({ $hasActiveSimulation }) => ($hasActiveSimulation ? 0.5 : 1)};
`;

const StyledThumbnail = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const StyledPlaceholderImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
`;

const StyledStatusBadge = styled.div<{ $status?: string }>`
  background: ${({ $status }) =>
    $status === 'completed' ? 'var(--red-main)' : 'var(--yellow-dark)'};
  color: var(--white-main);
  padding: 0 var(--small-padding);
  border-radius: var(--small-border-radius);
  line-height: var(--small-line-height);
  text-transform: capitalize;
  display: none;
  margin-left: var(--xsmall-padding);
  white-space: nowrap;
  height: auto;
  align-items: center;
`;

const StyledCardContent = styled.div`
  position: relative;

  &:hover {
    .calendar-icon {
      display: ${({ theme }) => (theme.hasActive ? 'none' : 'block')};
    }

    .status-badge {
      display: ${({ theme }) => (theme.hasActive ? 'block' : 'none')};
      height: 20px;
    }

    .difficulty-rating {
      display: ${({ theme }) => (theme.hasActive ? 'none' : 'flex')};
    }
  }
`;

const StyledButtonContainer = styled(FlexContainer)`
  width: 90%;

  .p-button {
    transition: all 0.2s ease;
    white-space: nowrap;

    &.primary-action-button {
      background: var(--red-dark);
      color: var(--white-main);
      border-radius: var(--heading-medium-font-size);
      padding: var(--xsmall-padding) var(--heading-medium-font-size);
      font-weight: 600;
      font-size: var(--small-font-size);

      &:hover:not(:disabled) {
        background: var(--red-darkest);
      }
    }

    &.preview-button {
      background: transparent !important;
      border: none !important;
      box-shadow: none !important;
      color: var(--black-main);
      padding: var(--xsmall-padding) var(--small-padding);
      font-weight: 400;
      font-size: var(--small-font-size);
      position: relative;
      display: inline-flex;
      justify-content: center;
      align-items: center;

      .p-button-label {
        border-bottom: var(--xxxsmall-padding) solid var(--gray-dark);
        padding-bottom: var(--xxsmall-padding);
      }

      &:hover {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        color: var(--black-main) !important;

        .p-button-label {
          border-bottom-color: var(--red-darkest);
        }
      }

      &:focus {
        background: transparent !important;
        box-shadow: none !important;
      }
    }
  }
`;

type CampaignCatalogueCardProps = {
  campaign: CampaignCatalogueItem;
  onPreview: (id: string) => void;
  onSelect: (id: string) => void;
  mode: 'create' | 'select';
  canPerformAction: boolean;
  isFreeTrial: boolean;
};

export const CampaignCatalogueCardV2: React.FC<CampaignCatalogueCardProps> = ({
  campaign,
  onPreview,
  onSelect,
  mode,
  canPerformAction,
  isFreeTrial,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canUseFeature } = useFeatureFlag();
  const { language } = useContext(LanguageContext);
  const [showMetrics, setShowMetrics] = useState(false);

  const statusMessage =
    campaign.simulationStatus &&
    CampaignStatusMessages[campaign.simulationStatus]
      ? t(CampaignStatusMessages[campaign.simulationStatus])
      : null;

  const hasMetrics =
    campaign.benchmarkClickRate !== null ||
    campaign.benchmarkSubmitRate !== null;

  const labels = phishingSignsLabels(t);
  const phishingSignLabel = campaign.phishingSign
    ? labels[campaign.phishingSign]
    : undefined;

  return (
    <StyledCard
      $hasActiveSimulation={campaign.hasActiveSimulation ?? false}
      onMouseEnter={() => {
        if (hasMetrics) setShowMetrics(true);
      }}
      onMouseLeave={() => {
        setShowMetrics(false);
      }}
    >
      <StyledCardContent theme={{ hasActive: campaign.hasActiveSimulation }}>
        <StyledTopRow justify="space-between" align="center">
          <FlexContainer align="center" justify="flex-start" gap={0}>
            {campaign.phishingSign && (
              <>
                <AppChip
                  label={phishingSignLabel}
                  type={`phishing-${campaign.phishingSign}` as any}
                />
                {campaign.hasActiveSimulation && (
                  <StyledCalendarIcon
                    className="calendar-icon"
                    src={CalendarIcon}
                    alt="Calendar"
                  />
                )}
                {campaign.hasActiveSimulation && statusMessage && (
                  <StyledStatusBadge
                    className="status-badge"
                    $status={campaign.simulationStatus?.toLowerCase()}
                  >
                    {statusMessage}
                  </StyledStatusBadge>
                )}
              </>
            )}
          </FlexContainer>
          <FlexContainer justify="flex-end" className="difficulty-rating">
            {campaign.difficulty && (
              <StarRating
                value={campaign.difficulty}
                maxStars={5}
                icon="fish"
                disabled
                greyedOut={campaign.hasActiveSimulation}
              />
            )}
          </FlexContainer>
        </StyledTopRow>

        <StyledCampaignName>{campaign.name}</StyledCampaignName>

        {campaign.language && (
          <StyledLanguageLabel>
            {`${t('generic.lang')}: ${
              languageDisplayLabel[campaign.language]?.match(/\[(.+)\]/)?.[1] ??
              campaign.language
            }`}
          </StyledLanguageLabel>
        )}

        <StyledDescription
          dangerouslySetInnerHTML={{
            __html:
              campaign.description || t('campaign.template.noDescription'),
          }}
        />

        <StyledThumbnailContainer
          $hasActiveSimulation={campaign.hasActiveSimulation}
        >
          {campaign.thumbnailUrl ? (
            <StyledThumbnail
              src={campaign.thumbnailUrl}
              alt={campaign.name}
              loading="lazy"
            />
          ) : (
            <StyledPlaceholderImage
              src={CampaignThumbnailPlaceholder}
              alt={t('campaign.template.imagePlaceholder')}
            />
          )}

          {hasMetrics && (
            <CatalogueCardMetrics
              clickRate={campaign.benchmarkClickRate}
              submitRate={campaign.benchmarkSubmitRate}
              visible={showMetrics}
              campaignId={campaign.id}
            />
          )}
        </StyledThumbnailContainer>

        <StyledButtonContainer justify="flex-start" align="center" gap={8}>
          {isFreeTrial &&
          mode === 'create' &&
          canUseFeature(Subjects.SELF_SERVICE_AUTO_PHISHING) ? (
            <AppButton
              label={t('campaign.template.bookFreeSimulation')}
              className="primary-action-button"
              onClick={() =>
                navigate(
                  RedirectPaths[RedirectPathsEnum.PHISHING_SIMULATIONS](),
                )
              }
            />
          ) : !canPerformAction ? (
            <a
              href={
                language === LanguagesEnum.DK
                  ? PHISHING_BOOK_DEMO_DK
                  : PHISHING_BOOK_DEMO_EN
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              <AppButton
                label={t('campaign.template.bookFreeSimulation')}
                className="primary-action-button"
              />
            </a>
          ) : canPerformAction ? (
            <AppButton
              label={
                mode === 'create'
                  ? t('campaign.template.createSimulation')
                  : t('campaign.template.selectTemplate')
              }
              onClick={() => onSelect(campaign.id)}
              className="primary-action-button"
            />
          ) : null}
          <AppButton
            label={t('generic.preview')}
            onClick={() => onPreview(campaign.id)}
            className="preview-button"
          />
        </StyledButtonContainer>
      </StyledCardContent>
    </StyledCard>
  );
};
